import { computed, inject, signal, type Signal, type WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, defer, distinctUntilChanged, map, of, startWith, switchMap, type Observable } from 'rxjs';
import { mapWishlistRequestError } from '../errors/map-wishlist-request-error';
import { WISHLIST_READ_CONTEXT } from '../constants/wishlist-read-context.token';
import type { WishlistContextRead } from '../types/wishlist-context-read.interface';
import type { WishlistReadRequest } from '../types/wishlist-read-request.interface';
import type { WishlistReadResult } from '../types/wishlist-read-result.interface';
import type { WishlistRequestState } from '../types/wishlist-request-state.type';

export function createWishlistContextRead<T>(load: () => Observable<WishlistRequestState<T>>, routeKey: Signal<string>, scope: Signal<unknown | null> = inject(WISHLIST_READ_CONTEXT)): WishlistContextRead<T> {
  const revision: WritableSignal<number> = signal<number>(0);
  const request: Signal<WishlistReadRequest> = computed((): WishlistReadRequest => ({ scope: scope(), route: routeKey(), revision: revision() }));
  const loading: WishlistRequestState<T> = { status: 'loading', data: null, error: null };
  const unavailable: WishlistRequestState<T> = {
    status: 'error', data: null,
    error: { statusCode: 401, message: 'Ten kontekst nie jest już dostępny.', title: null, type: null, traceId: null },
  };
  const result: Signal<WishlistReadResult<T> | undefined> = toSignal(toObservable(request).pipe(
    startWith(request()), distinctUntilChanged(),
    switchMap((current: WishlistReadRequest): Observable<WishlistReadResult<T>> => (current.scope === null ? of(unavailable) : defer(load).pipe(
      catchError((error: unknown): Observable<WishlistRequestState<T>> => of({ status: 'error', data: null, error: mapWishlistRequestError(error) })),
      startWith(loading),
    )).pipe(
      map((state: WishlistRequestState<T>): WishlistReadResult<T> => ({ request: current, state })),
    )),
  ));
  return {
    state: computed((): WishlistRequestState<T> => {
      const current: WishlistReadRequest = request();
      if (current.scope === null) return unavailable;
      const loaded: WishlistReadResult<T> | undefined = result();
      return loaded?.request === current ? loaded.state : loading;
    }),
    retry: (): void => revision.update((value: number): number => value + 1),
  };
}
